import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { ArrowDown, ArrowUp, Check, Copy, Mail, MessageSquare, Plus, ShieldCheck, Star, Trash2, Type, Wrench, X } from 'lucide-react';
import api from '../../services/api';
import { RichTextEditor } from '../RichTextEditor';
import {
  CampagneDetail,
  CampagneInvoer,
  CampagneStandaarden,
  CommunicatieBericht,
  Moment,
  getCampagneStandaarden,
  maakCampagne,
  planCampagne,
  werkCampagneBij,
} from '../../services/dataCockpitApi';

const STAPPEN = ['campagne', 'vragen', 'planning', 'communicatie', 'controle'] as const;
type Stap = (typeof STAPPEN)[number];
const MOMENTEN: Moment[] = ['VOORAANKONDIGING', 'LIVEGANG', 'HERINNERING'];

const invoerKlasse = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#0B1F3A] focus:outline-none focus:ring-1 focus:ring-[#0B1F3A]';
const DAG = 86_400_000;
const pad = (n: number) => String(n).padStart(2, '0');

/** ISO → waarde voor <input type="date"> in lokale tijd. */
const naarDatum = (iso: string | null) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
/** Datum + vast uur (lokale tijd) → ISO. */
const datumOp = (datum: string, uur: number, minuut = 0) => {
  if (!datum) return null;
  const [j, m, d] = datum.split('-').map(Number);
  return new Date(j, m - 1, d, uur, minuut).toISOString();
};
const opUur = (basis: Date, dagen: number, uur: number) => new Date(basis.getFullYear(), basis.getMonth(), basis.getDate() + dagen, uur, 0);
const tijdstip = (d: Date) => d.toLocaleString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const VOORBEELD = { bedrijfsnaam: 'Uw organisatie', einddatum: '', titel: '', link: 'https://belagom.nl/s/…' };

/** Realtime mockup van de e-mail zoals de medewerker hem ontvangt (zelfde opbouw als de server). */
function voorbeeldHtml(moment: Moment, c: CommunicatieBericht, std: CampagneStandaarden | undefined, waarden: typeof VOORBEELD) {
  const vul = (s: string) => s.replace(/\{\{\s*(bedrijfsnaam|titel|einddatum|link)\s*\}\}/gi, (_, k: keyof typeof VOORBEELD) => esc(waarden[k] || '…'));
  const label = std?.knop_label?.[moment] ?? null;
  const knop = label
    ? `<a style="display:inline-block;background:#0B1F3A;color:#fff;font-weight:bold;text-decoration:none;padding:14px 28px;border-radius:8px;">${esc(label)}</a>`
    : '';
  let body = vul(c.inhoud_html || '');
  body = /\{\{\s*knop\s*\}\}/i.test(body) ? body.replace(/\{\{\s*knop\s*\}\}/gi, knop) : label ? `${body}<p>${knop}</p>` : body;
  return `<!doctype html><html><head><meta charset="utf-8"><style>body{margin:0;background:#f3f4f6;font-family:Arial,sans-serif;color:#1a1a1a;font-size:14px}
    .kop{padding:16px 24px;font-weight:bold;color:#0B1F3A;font-size:18px}.inhoud{background:#fff;margin:0 12px 12px;padding:24px;border:1px solid #e5e7eb;border-radius:8px;line-height:1.6}
    hr{border:none;border-top:1px solid #e5e7eb;margin:20px 0}.klein{color:#6b7280;font-size:11px;margin:0}</style></head>
    <body><div class="kop">BeLagom</div><div class="inhoud">${body}<hr><p class="klein">Je antwoorden zijn anoniem: je werkgever ziet alleen groepsgemiddelden vanaf 10 deelnemers, nooit je naam of individuele antwoorden.</p></div></body></html>`;
}

/** Lengte van de SMS zoals hij de deur uitgaat ({{link}} → echte scanlink). */
const GSM7_BASIS = new Set("@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà");
const GSM7_EXTENSIE = new Set('^{}\\[~]|€\f');

/**
 * Lengte van de SMS zoals hij de deur uitgaat ({{link}} → echte link) en het
 * maximum voor 1 bericht: 160 bij GSM-7, 70 zodra er een emoji of ander
 * speciaal teken in staat (UCS-2). Zelfde regels als de server (smsLengte).
 */
function smsEcht(tekst: string, linkLengte: number): { lengte: number; max: number; gsm7: boolean } {
  const vol = tekst.replace(/\{\{\s*link\s*\}\}/gi, 'x'.repeat(linkLengte));
  let lengte = 0;
  for (const ch of vol) {
    if (GSM7_BASIS.has(ch)) lengte += 1;
    else if (GSM7_EXTENSIE.has(ch)) lengte += 2;
    else return { lengte: vol.length, max: 70, gsm7: false };
  }
  return { lengte, max: 160, gsm7: true };
}

/**
 * De 5-staps Campagne-Studio: campagne & doelgroep → Vragen-Studio (TNO-set
 * of eigen vragenlijst) → planning → Communicatie-Studio (met realtime
 * mockup) → controle. Opslaan = concept; "Opslaan & inplannen" zet hem op
 * GEPLAND zodat de scheduler de berichten op t-2 / t=0 10:00 / t+5 14:00 verstuurt.
 */
export const CampagneWizard: React.FC<{ bestaand?: CampagneDetail | null; onSluit: () => void }> = ({ bestaand, onSluit }) => {
  const { t } = useTranslation('employer');
  const qc = useQueryClient();
  const std = useQuery({ queryKey: ['data-campagne-standaarden'], queryFn: getCampagneStandaarden, staleTime: Infinity });
  const locaties = useQuery({ queryKey: ['employer-locations'], queryFn: async () => (await api.get('/employer/locations')).data?.data as { id: string; naam: string }[] });
  const afdelingen = useQuery({
    queryKey: ['employer-departments'],
    queryFn: async () => (await api.get('/employer/departments')).data?.data as { id: string; naam: string; location_id: string | null }[],
  });

  const [stap, setStap] = useState<Stap>('campagne');
  const [invoer, setInvoer] = useState<CampagneInvoer>(() => ({
    titel: bestaand?.titel ?? '',
    soort: bestaand?.soort ?? 'STANDAARD',
    vragen:
      bestaand?.soort === 'CUSTOM'
        ? bestaand.vragen.map((v) => ({ type: v.type, tekst: v.tekst, label_laag: v.label_laag, label_hoog: v.label_hoog }))
        : [{ type: 'STERREN', tekst: '' }],
    location_id: bestaand?.location_id ?? null,
    department_id: bestaand?.department_id ?? null,
    start_op: bestaand?.start_op ?? null,
    eind_op: bestaand?.eind_op ?? null,
    kanaal_email: bestaand?.kanaal_email ?? true,
    kanaal_sms: bestaand?.kanaal_sms ?? false,
    communicatie: bestaand?.communicatie ?? [],
  }));
  const [actiefMoment, setActiefMoment] = useState<Moment>('VOORAANKONDIGING');
  const zet = (patch: Partial<CampagneInvoer>) => setInvoer((i) => ({ ...i, ...patch }));
  const zetVraag = (i: number, patch: Partial<CampagneInvoer['vragen'][number]>) =>
    zet({ vragen: invoer.vragen.map((x, j) => (j === i ? { ...x, ...patch } : x)) });

  const max = std.data?.max_custom_vragen ?? 25;
  const smsMax = std.data?.sms_max ?? 160;
  const linkLengteVoor = (m: Moment) => (m === 'VOORAANKONDIGING' ? std.data?.portaal_link_lengte ?? 60 : std.data?.link_lengte ?? 52);
  const smsTeLang = (c: CommunicatieBericht) => {
    const e = smsEcht(c.sms_tekst ?? '', linkLengteVoor(c.moment));
    return Boolean(c.sms_tekst) && e.lengte > e.max;
  };
  const livegangUur = std.data?.livegang_uur ?? 10;
  const herinneringUur = std.data?.herinnering_uur ?? 14;
  const communicatie: CommunicatieBericht[] = useMemo(
    () =>
      MOMENTEN.map(
        (m) =>
          invoer.communicatie.find((c) => c.moment === m) ??
          std.data?.communicatie.find((c) => c.moment === m) ?? { moment: m, onderwerp: '', inhoud_html: '', sms_tekst: '' }
      ),
    [invoer.communicatie, std.data]
  );
  const zetBericht = (moment: Moment, patch: Partial<CommunicatieBericht>) =>
    zet({ communicatie: communicatie.map((c) => (c.moment === moment ? { ...c, ...patch } : c)) });

  const afdelingOpties = (afdelingen.data ?? []).filter((a) => !invoer.location_id || !a.location_id || a.location_id === invoer.location_id);
  const doelgroepNaam =
    (invoer.department_id && afdelingen.data?.find((a) => a.id === invoer.department_id)?.naam) ||
    (invoer.location_id && locaties.data?.find((l) => l.id === invoer.location_id)?.naam) ||
    t('dataCockpit.wizard.heleOrganisatie');
  const vragenLijst = invoer.soort === 'STANDAARD' ? std.data?.standaard_vragen ?? [] : invoer.vragen;

  // Per stap: wat ontbreekt er nog?
  const fouten: Record<Stap, string | null> = {
    campagne: invoer.titel.trim() ? null : t('dataCockpit.wizard.foutTitel'),
    vragen:
      invoer.soort === 'STANDAARD'
        ? null
        : invoer.vragen.length === 0
          ? t('dataCockpit.wizard.foutGeenVragen')
          : invoer.vragen.some((v) => !v.tekst.trim())
            ? t('dataCockpit.wizard.foutLegeVraag')
            : !invoer.vragen.some((v) => v.type === 'STERREN')
              ? t('dataCockpit.wizard.foutSterren')
              : null,
    planning:
      !invoer.start_op || !invoer.eind_op
        ? t('dataCockpit.wizard.foutDatums')
        : new Date(invoer.eind_op) <= new Date(invoer.start_op)
          ? t('dataCockpit.wizard.foutVolgorde')
          : !invoer.kanaal_email && !invoer.kanaal_sms
            ? t('dataCockpit.wizard.foutKanaal')
            : null,
    communicatie: invoer.kanaal_sms && communicatie.some(smsTeLang) ? t('dataCockpit.wizard.foutSms') : null,
    controle: null,
  };
  const stapIndex = STAPPEN.indexOf(stap);

  const opslaan = useMutation({
    mutationFn: async (inplannen: boolean) => {
      const body = { ...invoer, vragen: invoer.soort === 'STANDAARD' ? [] : invoer.vragen, communicatie };
      const { id } = bestaand ? await werkCampagneBij(bestaand.id, body) : await maakCampagne(body);
      if (inplannen) await planCampagne(id);
      return inplannen;
    },
    onSuccess: (inplannen) => {
      toast.success(inplannen ? t('dataCockpit.wizard.ingepland') : t('dataCockpit.wizard.opgeslagen'));
      qc.invalidateQueries({ queryKey: ['data-campagnes'] });
      onSluit();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || t('dataCockpit.fout')),
  });

  const start = invoer.start_op ? new Date(invoer.start_op) : null;
  const momentDatum = (m: Moment) => {
    if (!start) return '—';
    if (m === 'HERINNERING') return tijdstip(opUur(start, std.data?.herinnering_dagen ?? 5, herinneringUur));
    if (m === 'VOORAANKONDIGING') return tijdstip(new Date(start.getTime() - (std.data?.vooraankondiging_dagen ?? 2) * DAG));
    return tijdstip(start);
  };
  const waarden = {
    ...VOORBEELD,
    titel: invoer.titel || '…',
    einddatum: invoer.eind_op ? new Date(invoer.eind_op).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }) : '',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" role="dialog" aria-modal="true" aria-labelledby="wizard-titel">
      <div className={`my-6 w-full rounded-2xl bg-white shadow-xl ${stap === 'communicatie' ? 'max-w-6xl' : 'max-w-3xl'}`}>
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <h2 id="wizard-titel" className="text-lg font-semibold text-gray-900">
            {bestaand ? t('dataCockpit.wizard.titelBewerken') : t('dataCockpit.wizard.titelNieuw')}
          </h2>
          <button type="button" onClick={onSluit} className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100" aria-label={t('dataCockpit.sluiten')}>
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Stappenbalk */}
        <ol className="flex gap-1 overflow-x-auto border-b border-gray-100 px-6 py-3 text-xs">
          {STAPPEN.map((s, i) => (
            <li key={s}>
              <button
                type="button"
                onClick={() => setStap(s)}
                aria-current={s === stap ? 'step' : undefined}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 font-medium ${
                  s === stap ? 'bg-[#0B1F3A] text-white' : i < stapIndex ? 'bg-blue-50 text-[#0B1F3A]' : 'bg-gray-100 text-gray-600'
                }`}
              >
                <span>{i + 1}</span>
                {t(`dataCockpit.wizard.stap.${s}`)}
                {i < stapIndex && !fouten[s] && <Check className="h-3.5 w-3.5" aria-hidden />}
              </button>
            </li>
          ))}
        </ol>

        <div className="space-y-4 px-6 py-5">
          {/* ── Stap 1: Campagne-identificatie & doelgroep ── */}
          {stap === 'campagne' && (
            <>
              <label className="block text-sm font-medium text-gray-700">
                {t('dataCockpit.wizard.titel')}
                <input className={`${invoerKlasse} mt-1`} maxLength={200} value={invoer.titel} onChange={(e) => zet({ titel: e.target.value })} placeholder={t('dataCockpit.wizard.titelPlaceholder')} />
              </label>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block text-sm font-medium text-gray-700">
                  {t('dataCockpit.wizard.vestiging')}
                  <select className={`${invoerKlasse} mt-1`} value={invoer.location_id ?? ''} onChange={(e) => zet({ location_id: e.target.value || null, department_id: null })}>
                    <option value="">{t('dataCockpit.wizard.alleVestigingen')}</option>
                    {(locaties.data ?? []).map((l) => <option key={l.id} value={l.id}>{l.naam}</option>)}
                  </select>
                </label>
                <label className="block text-sm font-medium text-gray-700">
                  {t('dataCockpit.wizard.afdeling')}
                  <select className={`${invoerKlasse} mt-1`} value={invoer.department_id ?? ''} onChange={(e) => zet({ department_id: e.target.value || null })}>
                    <option value="">{t('dataCockpit.wizard.alleAfdelingen')}</option>
                    {afdelingOpties.map((a) => <option key={a.id} value={a.id}>{a.naam}</option>)}
                  </select>
                </label>
              </div>
              <p className="text-xs text-gray-500">{t('dataCockpit.wizard.doelgroepUitleg')}</p>
            </>
          )}

          {/* ── Stap 2: Vragen-Studio ── */}
          {stap === 'vragen' && (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                {(['STANDAARD', 'CUSTOM'] as const).map((s) => (
                  <button
                    key={s}
                    type="button"
                    aria-pressed={invoer.soort === s}
                    onClick={() => zet({ soort: s })}
                    className={`rounded-xl border p-4 text-left ${invoer.soort === s ? 'border-[#0B1F3A] bg-blue-50 ring-1 ring-[#0B1F3A]' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <p className="flex items-center gap-2 font-semibold text-gray-900">
                      {s === 'STANDAARD' ? <ShieldCheck className="h-4 w-4 text-[#0B1F3A]" aria-hidden /> : <Wrench className="h-4 w-4 text-gray-600" aria-hidden />}
                      {t(`dataCockpit.wizard.soort.${s}`)}
                    </p>
                    <p className="mt-1 text-xs text-gray-600">{t(`dataCockpit.wizard.soortUitleg.${s}`, { max })}</p>
                  </button>
                ))}
              </div>

              {invoer.soort === 'STANDAARD' && (
                <div>
                  <p className="mb-3 text-sm text-gray-600">{t('dataCockpit.wizard.standaardUitleg')}</p>
                  <ol className="space-y-2">
                    {(std.data?.standaard_vragen ?? []).map((v) => (
                      <li key={v.volgorde} className="flex gap-3 rounded-lg bg-gray-50 px-3 py-2.5 text-sm">
                        {v.type === 'STERREN' ? <Star className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" aria-hidden /> : <Type className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-500" aria-hidden />}
                        <div>
                          <p className="text-gray-900">{v.tekst}</p>
                          <p className="mt-0.5 text-xs text-gray-500">
                            {v.type === 'STERREN'
                              ? t('dataCockpit.wizard.schaal', { laag: v.label_laag, hoog: v.label_hoog })
                              : t('dataCockpit.wizard.openSanitizer')}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {invoer.soort === 'CUSTOM' && (
                <div>
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm text-gray-600">{t('dataCockpit.wizard.customUitleg')}</p>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => zet({ vragen: (std.data?.standaard_vragen ?? []).map((v) => ({ type: v.type, tekst: v.tekst, label_laag: v.label_laag, label_hoog: v.label_hoog })) })}
                        className="inline-flex items-center gap-1 text-xs font-medium text-[#0B1F3A] hover:underline"
                      >
                        <Copy className="h-3.5 w-3.5" aria-hidden /> {t('dataCockpit.wizard.tnoAlsBasis')}
                      </button>
                      <span className={`text-xs font-medium ${invoer.vragen.length >= max ? 'text-red-700' : 'text-gray-500'}`}>{invoer.vragen.length} / {max}</span>
                    </div>
                  </div>
                  <ol className="space-y-2">
                    {invoer.vragen.map((v, i) => (
                      <li key={i} className="rounded-lg border border-gray-200 p-3">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                          <span className="text-xs font-semibold text-gray-400">{i + 1}.</span>
                          <input
                            className={invoerKlasse}
                            maxLength={500}
                            value={v.tekst}
                            aria-label={t('dataCockpit.wizard.vraagTekst', { n: i + 1 })}
                            placeholder={t('dataCockpit.wizard.vraagPlaceholder')}
                            onChange={(e) => zetVraag(i, { tekst: e.target.value })}
                          />
                          <div className="flex items-center gap-1">
                            <div className="inline-flex rounded-lg border border-gray-200 p-0.5">
                              {(['STERREN', 'OPEN'] as const).map((ty) => (
                                <button
                                  key={ty}
                                  type="button"
                                  title={t(`dataCockpit.wizard.type.${ty}`)}
                                  aria-label={t(`dataCockpit.wizard.type.${ty}`)}
                                  aria-pressed={v.type === ty}
                                  onClick={() => zetVraag(i, { type: ty })}
                                  className={`rounded-md p-1.5 ${v.type === ty ? 'bg-[#0B1F3A] text-white' : 'text-gray-500 hover:bg-gray-50'}`}
                                >
                                  {ty === 'STERREN' ? <Star className="h-4 w-4" /> : <Type className="h-4 w-4" />}
                                </button>
                              ))}
                            </div>
                            <button type="button" disabled={i === 0} onClick={() => { const n = [...invoer.vragen]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; zet({ vragen: n }); }} className="rounded p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30" aria-label={t('dataCockpit.wizard.omhoog')}><ArrowUp className="h-4 w-4" /></button>
                            <button type="button" disabled={i === invoer.vragen.length - 1} onClick={() => { const n = [...invoer.vragen]; [n[i + 1], n[i]] = [n[i], n[i + 1]]; zet({ vragen: n }); }} className="rounded p-1.5 text-gray-500 hover:bg-gray-100 disabled:opacity-30" aria-label={t('dataCockpit.wizard.omlaag')}><ArrowDown className="h-4 w-4" /></button>
                            <button type="button" onClick={() => zet({ vragen: invoer.vragen.filter((_, j) => j !== i) })} className="rounded p-1.5 text-red-600 hover:bg-red-50" aria-label={t('dataCockpit.wizard.verwijderVraag')}><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </div>
                        {v.type === 'STERREN' && (
                          <div className="mt-2 grid gap-2 sm:ml-5 sm:grid-cols-2">
                            <input className={`${invoerKlasse} text-xs`} maxLength={120} value={v.label_laag ?? ''} onChange={(e) => zetVraag(i, { label_laag: e.target.value })} placeholder={t('dataCockpit.wizard.labelLaag')} aria-label={t('dataCockpit.wizard.labelLaag')} />
                            <input className={`${invoerKlasse} text-xs`} maxLength={120} value={v.label_hoog ?? ''} onChange={(e) => zetVraag(i, { label_hoog: e.target.value })} placeholder={t('dataCockpit.wizard.labelHoog')} aria-label={t('dataCockpit.wizard.labelHoog')} />
                          </div>
                        )}
                      </li>
                    ))}
                  </ol>
                  <button
                    type="button"
                    disabled={invoer.vragen.length >= max}
                    onClick={() => zet({ vragen: [...invoer.vragen, { type: 'STERREN', tekst: '' }] })}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                  >
                    <Plus className="h-4 w-4" /> {t('dataCockpit.wizard.vraagToevoegen')}
                  </button>
                </div>
              )}
            </>
          )}

          {/* ── Stap 3: Planning & kanalen ── */}
          {stap === 'planning' && (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block text-sm font-medium text-gray-700">
                  {t('dataCockpit.wizard.start', { uur: livegangUur })}
                  <input type="date" className={`${invoerKlasse} mt-1`} value={naarDatum(invoer.start_op)} onChange={(e) => zet({ start_op: datumOp(e.target.value, livegangUur) })} />
                </label>
                <label className="block text-sm font-medium text-gray-700">
                  {t('dataCockpit.wizard.eind')}
                  <input type="date" className={`${invoerKlasse} mt-1`} value={naarDatum(invoer.eind_op)} onChange={(e) => zet({ eind_op: datumOp(e.target.value, 23, 59) })} />
                </label>
              </div>
              <fieldset className="flex flex-wrap gap-4">
                <legend className="mb-1 text-sm font-medium text-gray-700">{t('dataCockpit.wizard.kanalen')}</legend>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={invoer.kanaal_email} onChange={(e) => zet({ kanaal_email: e.target.checked })} /> <Mail className="h-4 w-4 text-gray-500" aria-hidden /> {t('dataCockpit.wizard.email')}</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={invoer.kanaal_sms} onChange={(e) => zet({ kanaal_sms: e.target.checked })} /> <MessageSquare className="h-4 w-4 text-gray-500" aria-hidden /> {t('dataCockpit.wizard.sms')}</label>
              </fieldset>
              <div className="rounded-lg bg-gray-50 p-3 text-sm">
                <p className="mb-1 font-medium text-gray-700">{t('dataCockpit.wizard.tijdlijn')}</p>
                <ul className="space-y-0.5 text-gray-600">
                  {MOMENTEN.map((m) => <li key={m}>{t(`dataCockpit.wizard.moment.${m}`)}: <strong>{momentDatum(m)}</strong></li>)}
                </ul>
                <p className="mt-2 text-xs text-gray-500">{t('dataCockpit.wizard.tijdlijnUitleg', { uur: herinneringUur })}</p>
              </div>
            </div>
          )}

          {/* ── Stap 4: Communicatie-Studio met realtime mockup ── */}
          {stap === 'communicatie' && (
            <div>
              <p className="mb-3 text-sm text-gray-600">{t('dataCockpit.wizard.studioUitleg')}</p>
              <div className="mb-3 flex gap-1 border-b border-gray-200" role="tablist">
                {MOMENTEN.map((m) => (
                  <button
                    key={m}
                    type="button"
                    role="tab"
                    aria-selected={actiefMoment === m}
                    onClick={() => setActiefMoment(m)}
                    className={`-mb-px border-b-2 px-3 py-2 text-xs font-medium ${actiefMoment === m ? 'border-[#0B1F3A] text-[#0B1F3A]' : 'border-transparent text-gray-500'}`}
                  >
                    {t(`dataCockpit.wizard.moment.${m}`)}
                  </button>
                ))}
              </div>
              {communicatie.filter((c) => c.moment === actiefMoment).map((c) => {
                const echt = smsEcht(c.sms_tekst ?? '', linkLengteVoor(c.moment));
                const teLang = smsTeLang(c);
                const knop = std.data?.knop_label?.[c.moment];
                return (
                  <div key={c.moment} className="grid gap-5 lg:grid-cols-2">
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700">
                        {t('dataCockpit.wizard.onderwerp')}
                        <input className={`${invoerKlasse} mt-1`} maxLength={200} value={c.onderwerp} onChange={(e) => zetBericht(c.moment, { onderwerp: e.target.value })} />
                      </label>
                      <div>
                        <p className="mb-1 text-sm font-medium text-gray-700">{t('dataCockpit.wizard.emailTekst')}</p>
                        <RichTextEditor key={c.moment} simple content={c.inhoud_html} onChange={(html) => zetBericht(c.moment, { inhoud_html: html })} minHeight={200} />
                        {knop && <p className="mt-1 text-xs text-gray-500">{t('dataCockpit.wizard.knopUitleg', { label: knop })}</p>}
                      </div>
                      {invoer.kanaal_sms && (
                        <label className="block text-sm font-medium text-gray-700">
                          <span className="flex items-center justify-between">
                            {t('dataCockpit.wizard.smsTekst')}
                            <span
                              className={`text-xs font-semibold tabular-nums ${teLang ? 'text-red-700' : echt.lengte > echt.max - 15 ? 'text-amber-700' : 'text-gray-500'}`}
                              aria-live="polite"
                              title={t('dataCockpit.wizard.smsTellerUitleg')}
                            >
                              {c.sms_tekst ? echt.lengte : 0} / {echt.max}
                            </span>
                          </span>
                          <textarea
                            rows={3}
                            maxLength={smsMax}
                            className={`${invoerKlasse} mt-1 ${teLang ? 'border-red-400' : ''}`}
                            value={c.sms_tekst ?? ''}
                            placeholder={t('dataCockpit.wizard.geenSms')}
                            onChange={(e) => zetBericht(c.moment, { sms_tekst: e.target.value.replace(/\n/g, ' ') })}
                          />
                          <span className={`mt-1 block text-xs font-normal ${teLang ? 'text-red-700' : 'text-gray-500'}`}>
                            {teLang
                              ? t(echt.gsm7 ? 'dataCockpit.wizard.smsTeLang' : 'dataCockpit.wizard.smsTeLangUcs', { n: echt.lengte, max: echt.max })
                              : t('dataCockpit.wizard.smsTellerUitleg')}
                          </span>
                        </label>
                      )}
                      <p className="text-xs text-gray-500">
                        {t('dataCockpit.wizard.placeholders')} {(std.data?.placeholders ?? []).map((p) => <code key={p} className="mr-1 rounded bg-gray-100 px-1">{p}</code>)}
                      </p>
                      {c.verzonden_op && <p className="text-xs text-green-700">✓ {t('dataCockpit.wizard.verzonden', { n: c.aantal_verzonden ?? 0 })}</p>}
                    </div>

                    <div className="space-y-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{t('dataCockpit.wizard.preview')}</p>
                      <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
                        <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-xs text-gray-600">
                          <p><span className="text-gray-400">{t('dataCockpit.wizard.previewVan')}</span> BeLagom</p>
                          <p className="truncate font-semibold text-gray-900">{c.onderwerp.replace(/\{\{\s*titel\s*\}\}/g, waarden.titel) || '—'}</p>
                        </div>
                        {/* sandbox zonder allow-scripts: HR-HTML kan hier niets uitvoeren */}
                        <iframe title={t('dataCockpit.wizard.preview')} sandbox="" className="h-[26rem] w-full bg-gray-100" srcDoc={voorbeeldHtml(c.moment, c, std.data, waarden)} />
                      </div>
                      {invoer.kanaal_sms && c.sms_tekst && (
                        <div className="max-w-xs rounded-2xl rounded-bl-sm bg-gray-100 px-4 py-2.5 text-sm text-gray-800">
                          {c.sms_tekst.replace(/\{\{\s*link\s*\}\}/gi, VOORBEELD.link).replace(/\{\{\s*bedrijfsnaam\s*\}\}/gi, VOORBEELD.bedrijfsnaam)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Stap 5: Controle ── */}
          {stap === 'controle' && (
            <dl className="divide-y divide-gray-100 rounded-lg border border-gray-200 text-sm">
              {[
                [t('dataCockpit.wizard.titel'), invoer.titel || '—'],
                [t('dataCockpit.wizard.stap.campagne'), doelgroepNaam],
                [t('dataCockpit.wizard.stap.vragen'), `${t(`dataCockpit.wizard.soort.${invoer.soort}`)} · ${t('dataCockpit.campagnes.vragen', { n: vragenLijst.length })}`],
                ...MOMENTEN.map((m) => [t(`dataCockpit.wizard.moment.${m}`), momentDatum(m)]),
                [t('dataCockpit.wizard.eind'), invoer.eind_op ? tijdstip(new Date(invoer.eind_op)) : '—'],
                [t('dataCockpit.wizard.kanalen'), [invoer.kanaal_email && t('dataCockpit.wizard.email'), invoer.kanaal_sms && t('dataCockpit.wizard.sms')].filter(Boolean).join(' + ') || '—'],
              ].map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-3 px-4 py-2.5">
                  <dt className="text-gray-500">{k}</dt>
                  <dd className="col-span-2 font-medium text-gray-900">{v}</dd>
                </div>
              ))}
              <div className="flex gap-2 bg-blue-50 px-4 py-3 text-xs text-blue-900">
                <ShieldCheck className="h-4 w-4 flex-shrink-0" aria-hidden />
                <p>{t('dataCockpit.wizard.privacyControle')}</p>
              </div>
              {STAPPEN.some((s) => fouten[s]) && (
                <ul className="list-disc px-8 py-3 text-xs text-red-700">
                  {STAPPEN.filter((s) => fouten[s]).map((s) => <li key={s}>{t(`dataCockpit.wizard.stap.${s}`)}: {fouten[s]}</li>)}
                </ul>
              )}
            </dl>
          )}

          {stap !== 'controle' && fouten[stap] && <p className="text-sm text-red-700">{fouten[stap]}</p>}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 px-6 py-4">
          <button type="button" disabled={stapIndex === 0} onClick={() => setStap(STAPPEN[stapIndex - 1])} className="rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-30">
            {t('dataCockpit.wizard.vorige')}
          </button>
          <div className="flex flex-wrap gap-2">
            {stapIndex < STAPPEN.length - 1 && (
              <button type="button" disabled={Boolean(fouten[stap])} onClick={() => setStap(STAPPEN[stapIndex + 1])} className="rounded-lg bg-[#0B1F3A] px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
                {t('dataCockpit.wizard.volgende')}
              </button>
            )}
            {stapIndex >= STAPPEN.indexOf('communicatie') && (
              <button
                type="button"
                disabled={opslaan.isPending || Boolean(fouten.campagne || fouten.vragen || fouten.communicatie)}
                onClick={() => opslaan.mutate(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                {t('dataCockpit.wizard.opslaanConcept')}
              </button>
            )}
            {stap === 'controle' && (
              <button
                type="button"
                disabled={opslaan.isPending || STAPPEN.some((s) => fouten[s])}
                onClick={() => opslaan.mutate(true)}
                className="rounded-lg bg-[#0B1F3A] px-4 py-2 text-sm font-semibold text-white disabled:opacity-40"
              >
                {t('dataCockpit.wizard.opslaanInplannen')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampagneWizard;
